/**
 * ドロワーカート: ショッパーリスト（metaobject shopper_list / フィールド shopper_products）
 */

const ROOT_SELECTOR = "[data-js-cart-shopper-list]";
const CONFIG_SELECTOR = "[data-js-cart-shopper-list-config]";
const CHECKBOX_SELECTOR = "[data-js-cart-shopper-list-checkbox]";
const OPEN_SELECTOR = "[data-js-cart-shopper-list-open]";
const CHANGE_SELECTOR = "[data-js-cart-shopper-list-change]";
const SELECTED_SELECTOR = "[data-js-cart-shopper-list-selected]";
const SELECTED_LABEL_SELECTOR = "[data-js-cart-shopper-list-selected-label]";
const MODAL_SELECTOR = "[data-js-cart-shopper-modal]";
const MODAL_CLOSE_SELECTOR = "[data-js-cart-shopper-modal-close]";
const MODAL_SUBMIT_SELECTOR = "[data-js-cart-shopper-modal-submit]";
const MODAL_OPTION_SELECTOR = "[data-js-cart-shopper-modal-option]";
const ERROR_SELECTOR = "[data-js-cart-shopper-list-error]";
const SPINNER_SELECTOR = "[data-js-cart-shopper-list-spinner]";
const CHECKOUT_SELECTOR = "#CartDrawer-Checkout";
const STORAGE_KEY = "cart-shopper-list-selected-id";

type ShopperListConfig = {
  id: string;
  title: string;
  variantIds: number[];
};

type CartDrawerElement = HTMLElement & {
  renderContents?: (parsedState: Record<string, unknown>) => void;
  getSectionsToRender?: () => Array<{ id: string; selector?: string }>;
};

type RoutesWindow = Window & {
  routes?: {
    cart_add_url: string;
    cart_update_url: string;
    cart_url: string;
  };
  cartShopperListStrings?: {
    selectedLabel: string;
    error: string;
  };
};

function parseConfig(root: HTMLElement): ShopperListConfig[] {
  const configEl = root.querySelector(CONFIG_SELECTOR);
  if (!configEl?.textContent) return [];

  try {
    const parsed = JSON.parse(configEl.textContent) as ShopperListConfig[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function getCartDrawer(): CartDrawerElement | null {
  return document.querySelector("cart-drawer");
}

function getSectionIds(): string[] {
  const cartDrawer = getCartDrawer();
  const sections = cartDrawer?.getSectionsToRender?.().map((section) => section.id) ?? [];
  return sections.length > 0 ? sections : ["cart-drawer", "cart-icon-bubble"];
}

async function fetchCartVariantIds(): Promise<Set<number>> {
  const routes = (window as RoutesWindow).routes;
  if (!routes?.cart_url) return new Set();

  const response = await fetch(`${routes.cart_url}.js`);
  if (!response.ok) return new Set();

  const cart = (await response.json()) as { items?: Array<{ variant_id: number }> };
  return new Set((cart.items ?? []).map((item) => item.variant_id));
}

function areAllVariantsInCart(variantIds: number[], cartVariantIds: Set<number>): boolean {
  if (variantIds.length === 0) return false;
  return variantIds.every((id) => cartVariantIds.has(id));
}

function getMissingVariantIds(variantIds: number[], cartVariantIds: Set<number>): number[] {
  return variantIds.filter((id) => !cartVariantIds.has(id));
}

async function refreshCartSections(response: Record<string, unknown>): Promise<void> {
  const sections = response.sections as Record<string, string> | undefined;
  const cartDrawerSection = sections?.["cart-drawer"];
  if (!cartDrawerSection) return;

  const cartDrawer = getCartDrawer();
  const sourceRoot =
    new DOMParser().parseFromString(cartDrawerSection, "text/html").querySelector("#CartDrawer") ??
    new DOMParser().parseFromString(cartDrawerSection, "text/html");

  const itemCount = (response as { item_count?: number }).item_count;
  if (typeof itemCount === "number") {
    cartDrawer?.classList.toggle("is-empty", itemCount === 0);
  }

  // ショッパーリストは維持し、カート本体とフッターのみ部分更新（チェック状態のチラつき防止）
  for (const selector of ["cart-drawer-items", ".drawer__footer"] as const) {
    const targetElement = document.querySelector(selector);
    const sourceElement = sourceRoot.querySelector(selector);
    if (targetElement && sourceElement) {
      targetElement.replaceWith(sourceElement);
    }
  }

  const bubbleSection = sections?.["cart-icon-bubble"];
  if (bubbleSection) {
    const bubbleInner = new DOMParser()
      .parseFromString(bubbleSection, "text/html")
      .querySelector(".shopify-section")?.innerHTML;
    const bubbleTarget = document.getElementById("cart-icon-bubble");
    if (bubbleInner && bubbleTarget) {
      bubbleTarget.innerHTML = bubbleInner;
    }
  }

  document.dispatchEvent(new CustomEvent("cart-drawer:content-updated"));

  // フッター差し替え後も busy 中はチェックアウトを無効のまま維持
  const activeRoot = getActiveShopperListRoot();
  if (activeRoot?.hasAttribute("data-js-cart-shopper-list-busy")) {
    setCheckoutDisabled(true);
  }
}

async function addVariants(variantIds: number[]): Promise<void> {
  const routes = (window as RoutesWindow).routes;
  if (!routes?.cart_add_url || variantIds.length === 0) return;

  const cartVariantIds = await fetchCartVariantIds();
  const missingIds = getMissingVariantIds(variantIds, cartVariantIds);
  if (missingIds.length === 0) return;

  const response = await fetch(routes.cart_add_url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify({
      items: missingIds.map((id) => ({ id, quantity: 1 })),
      sections: getSectionIds(),
      sections_url: window.location.pathname,
    }),
  });

  const data = (await response.json()) as Record<string, unknown> & { status?: number; description?: string };
  if (data.status) {
    throw new Error(typeof data.description === "string" ? data.description : "add failed");
  }

  await refreshCartSections(data);
}

async function removeVariants(variantIds: number[]): Promise<void> {
  const routes = (window as RoutesWindow).routes;
  if (!routes?.cart_update_url || variantIds.length === 0) return;

  const updates: Record<string, number> = {};
  for (const id of variantIds) {
    updates[String(id)] = 0;
  }

  const response = await fetch(routes.cart_update_url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify({
      updates,
      sections: getSectionIds(),
      sections_url: window.location.pathname,
    }),
  });

  const data = (await response.json()) as Record<string, unknown> & { status?: number; description?: string };
  if (data.status) {
    throw new Error(typeof data.description === "string" ? data.description : "remove failed");
  }

  await refreshCartSections(data);
}

function showError(root: HTMLElement, message: string): void {
  const errorEl = root.querySelector<HTMLElement>(ERROR_SELECTOR);
  if (!errorEl) return;
  errorEl.textContent = message;
  errorEl.classList.remove("visually-hidden");
}

function hideError(root: HTMLElement): void {
  const errorEl = root.querySelector<HTMLElement>(ERROR_SELECTOR);
  if (!errorEl) return;
  errorEl.textContent = "";
  errorEl.classList.add("visually-hidden");
}

function setCheckoutDisabled(disabled: boolean): void {
  const checkout = document.querySelector<HTMLButtonElement>(CHECKOUT_SELECTOR);
  if (!checkout) return;
  checkout.disabled = disabled;
}

function setBusy(root: HTMLElement, busy: boolean): void {
  root.toggleAttribute("data-js-cart-shopper-list-busy", busy);

  const spinner = root.querySelector<HTMLElement>(SPINNER_SELECTOR);
  spinner?.classList.toggle("hidden", !busy);

  setCheckoutDisabled(busy);
}

function openModal(root: HTMLElement): void {
  const modal = root.querySelector<HTMLElement>(MODAL_SELECTOR);
  if (!modal) return;

  const storedId = sessionStorage.getItem(STORAGE_KEY);
  if (storedId) {
    const storedOption = root.querySelector<HTMLInputElement>(
      `${MODAL_OPTION_SELECTOR}[value="${CSS.escape(storedId)}"]`,
    );
    if (storedOption) storedOption.checked = true;
  }

  modal.hidden = false;
  const panel = modal.querySelector<HTMLElement>(".p-cart-drawer__shopper-modal-panel");
  panel?.focus();
}

function closeModal(root: HTMLElement): void {
  const modal = root.querySelector<HTMLElement>(MODAL_SELECTOR);
  if (!modal) return;
  modal.hidden = true;
}

function getSelectedListId(root: HTMLElement): string | null {
  const checked = root.querySelector<HTMLInputElement>(`${MODAL_OPTION_SELECTOR}:checked`);
  return checked?.value ?? null;
}

function findListById(lists: ShopperListConfig[], listId: string | number | null): ShopperListConfig | undefined {
  if (listId == null) return undefined;
  return lists.find((entry) => String(entry.id) === String(listId));
}

function updateMultipleUi(
  root: HTMLElement,
  lists: ShopperListConfig[],
  listId: string | number | null,
): void {
  const selectedWrap = root.querySelector<HTMLElement>(SELECTED_SELECTOR);
  const openButton = root.querySelector<HTMLElement>(OPEN_SELECTOR);
  const selectedLabel = root.querySelector<HTMLElement>(SELECTED_LABEL_SELECTOR);
  if (!selectedWrap || !openButton || !selectedLabel) return;

  const list = findListById(lists, listId);
  if (!list) {
    selectedWrap.classList.add("visually-hidden");
    openButton.classList.remove("visually-hidden");
    return;
  }

  const template = (window as RoutesWindow).cartShopperListStrings?.selectedLabel ?? "__TITLE__";
  selectedLabel.textContent = template.replaceAll("__TITLE__", list.title);
  selectedWrap.classList.remove("visually-hidden");
  openButton.classList.add("visually-hidden");
}

function getActiveShopperListRoot(): HTMLElement | null {
  return document.querySelector<HTMLElement>(ROOT_SELECTOR);
}

function syncMultipleUiFromStorage(root: HTMLElement = getActiveShopperListRoot() ?? document.body): void {
  const activeRoot = root.matches(ROOT_SELECTOR) ? root : getActiveShopperListRoot();
  if (!activeRoot) return;

  const lists = parseConfig(activeRoot);
  if (lists.length === 0) return;

  updateMultipleUi(activeRoot, lists, sessionStorage.getItem(STORAGE_KEY));
}

function initCartDrawerShopperList(root: HTMLElement): void {
  if (root.dataset.jsCartShopperListInitialized === "true") return;

  const lists = parseConfig(root);
  if (lists.length === 0) return;

  root.dataset.jsCartShopperListInitialized = "true";
  const mode = root.dataset.jsCartShopperListMode ?? "single";
  const errorMessage = (window as RoutesWindow).cartShopperListStrings?.error ?? "";

  const syncSingleCheckbox = async (): Promise<void> => {
    const checkbox = root.querySelector<HTMLInputElement>(CHECKBOX_SELECTOR);
    if (!checkbox) return;

    const cartVariantIds = await fetchCartVariantIds();
    checkbox.checked = areAllVariantsInCart(lists[0]?.variantIds ?? [], cartVariantIds);
  };

  if (mode === "single") {
    const checkbox = root.querySelector<HTMLInputElement>(CHECKBOX_SELECTOR);
    if (!checkbox) return;

    syncSingleCheckbox();

    checkbox.addEventListener("change", async () => {
      hideError(root);
      const intendedChecked = checkbox.checked;
      setBusy(root, true);
      checkbox.disabled = true;

      try {
        const variantIds = lists[0]?.variantIds ?? [];
        if (intendedChecked) {
          await addVariants(variantIds);
        } else {
          await removeVariants(variantIds);
        }
        // 部分更新後もユーザー操作の checked を維持
        checkbox.checked = intendedChecked;
      } catch {
        showError(root, errorMessage);
        checkbox.checked = !intendedChecked;
      } finally {
        checkbox.disabled = false;
        setBusy(root, false);
      }
    });
  } else {
    const storedId = sessionStorage.getItem(STORAGE_KEY);
    if (storedId) {
      updateMultipleUi(root, lists, storedId);
    }

    root.querySelector(OPEN_SELECTOR)?.addEventListener("click", () => openModal(root));
    root.querySelector(CHANGE_SELECTOR)?.addEventListener("click", () => openModal(root));

    for (const closeEl of Array.from(root.querySelectorAll<HTMLElement>(MODAL_CLOSE_SELECTOR))) {
      closeEl.addEventListener("click", () => closeModal(root));
    }

    root.querySelector(MODAL_SUBMIT_SELECTOR)?.addEventListener("click", async () => {
      hideError(root);
      const listId = getSelectedListId(root);
      const list = findListById(lists, listId);
      if (!list) return;

      setBusy(root, true);
      const submitButton = root.querySelector<HTMLButtonElement>(MODAL_SUBMIT_SELECTOR);
      if (submitButton) submitButton.disabled = true;

      try {
        const previousId = sessionStorage.getItem(STORAGE_KEY);
        const previousList = findListById(lists, previousId);

        // カート再描画前に保存して、section 更新後の再初期化でも新しい選択が反映される
        sessionStorage.setItem(STORAGE_KEY, String(list.id));

        if (previousList && String(previousList.id) !== String(list.id)) {
          await removeVariants(previousList.variantIds);
        }
        await addVariants(list.variantIds);
        syncMultipleUiFromStorage();
        closeModal(getActiveShopperListRoot() ?? root);
      } catch {
        showError(root, errorMessage);
      } finally {
        if (submitButton) submitButton.disabled = false;
        setBusy(root, false);
      }
    });
  }
}

export function initCartDrawerShopperListAll(root: ParentNode = document): void {
  for (const el of Array.from(root.querySelectorAll<HTMLElement>(ROOT_SELECTOR))) {
    if (el.dataset.jsCartShopperListInitialized === "true") {
      // 処理中は DOM を維持しているため同期しない（チェック状態のチラつき防止）
      if (el.hasAttribute("data-js-cart-shopper-list-busy")) {
        continue;
      }
      if (el.dataset.jsCartShopperListMode === "single") {
        const checkbox = el.querySelector<HTMLInputElement>(CHECKBOX_SELECTOR);
        if (checkbox) {
          fetchCartVariantIds().then((cartVariantIds) => {
            const lists = parseConfig(el);
            checkbox.checked = areAllVariantsInCart(lists[0]?.variantIds ?? [], cartVariantIds);
          });
        }
      } else {
        syncMultipleUiFromStorage(el);
      }
      continue;
    }
    initCartDrawerShopperList(el);
  }
}
